import type { JSX } from "preact";
import { cn } from "./cn";
import { useFieldControl } from "./field";

const BASE =
  "block w-full rounded-md border bg-bg px-3 py-2 text-sm text-fg " +
  "shadow-[var(--shadow-input)] outline-none transition " +
  "placeholder:text-fg-faint " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const VALID =
  "border-border focus:border-accent focus:ring-2 focus:ring-accent/50";
const INVALID =
  "border-danger-border focus:border-danger-fg focus:ring-2 focus:ring-danger-fg/50";

export type InputProps = Omit<
  JSX.IntrinsicElements["input"],
  "class" | "size"
> & {
  invalid?: boolean;
  monospace?: boolean;
  class?: string;
};

export function Input({
  invalid,
  monospace,
  class: extraClass,
  id,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: InputProps) {
  const field = useFieldControl(
    id as string | undefined,
    ariaDescribedBy as string | undefined,
  );
  return (
    <input
      {...rest}
      id={field.id}
      aria-describedby={field.describedBy}
      aria-invalid={invalid || undefined}
      class={cn(
        BASE,
        invalid ? INVALID : VALID,
        monospace && "font-mono",
        extraClass,
      )}
    />
  );
}

export type TextareaProps = Omit<
  JSX.IntrinsicElements["textarea"],
  "class" | "rows"
> & {
  invalid?: boolean;
  monospace?: boolean;
  rows?: number;
  class?: string;
};

export function Textarea({
  invalid,
  monospace,
  rows = 4,
  class: extraClass,
  id,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: TextareaProps) {
  const field = useFieldControl(
    id as string | undefined,
    ariaDescribedBy as string | undefined,
  );
  return (
    <textarea
      {...rest}
      id={field.id}
      aria-describedby={field.describedBy}
      rows={rows}
      aria-invalid={invalid || undefined}
      class={cn(
        BASE,
        invalid ? INVALID : VALID,
        monospace && "font-mono",
        extraClass,
      )}
    />
  );
}
